import React, { CSSProperties, useEffect, useState } from 'react';
import './Coupon.css';
import TextFill from '../text/TextFill';
import {
	alignToFlex,
	getHoverProps,
	getImageStyle,
	getMarginStyle,
	getPaddingStyle,
	getTextStyle,
	HoverKey,
	px,
} from '../styles';
import copyIcon from './assets/coupon-copy-icon.svg';
import glossyButton from './assets/coupon-glossy-button.svg';
import sparkleIcon from './assets/coupon-sparkle.svg';
import { cssUrl } from '../../../stickyBanner.helper';
import {
	ComponentSettings,
	MediaSettings,
	TypographySettings,
} from '../../../types';

interface CouponProps {
	settings: ComponentSettings;
	label: TypographySettings;
	code: TypographySettings;
	copiedText: TypographySettings;
	/** Text of the copy button inside the pill (gradient pill style). */
	copyButton?: TypographySettings;
	/** Ticket image (ticket variant). */
	ticket: MediaSettings;
	ticketUrl: string;
	/** Code box image (background "Image"). */
	boxImage?: MediaSettings;
	boxImageUrl?: string;
}

const isHex = ( value: string | undefined ): value is string =>
	!! value && /^#[0-9a-f]{3,8}$/i.test( value );

/**
 * Code box overrides from the "Code box" settings (any style with a box).
 * Uses background longhands so they replace the style's own background.
 *
 * @param style    Component style values.
 * @param image    Code box image settings.
 * @param imageUrl Code box image URL.
 * @param sized    The style's box can change size (not the ticket).
 */
const getBoxStyle = (
	style: Record< string, string >,
	image: MediaSettings | undefined,
	imageUrl: string,
	sized: boolean
): CSSProperties => {
	const box: CSSProperties = {};
	const type = style.boxBgType;
	if ( type === 'color' && isHex( style.boxBgColor ) ) {
		box.backgroundColor = style.boxBgColor;
		box.backgroundImage = 'none';
	} else if ( type === 'gradient' ) {
		box.backgroundColor = 'transparent';
		box.backgroundImage = `linear-gradient(${
			Number( style.boxGradAngle ) || 0
		}deg, ${ style.boxGradFrom } 0%, ${ style.boxGradTo } 100%)`;
	} else if ( type === 'image' && imageUrl ) {
		const width = px( image?.width ) || 'auto';
		const height = px( image?.height ) || 'auto';
		box.backgroundColor = 'transparent';
		box.backgroundImage = cssUrl( imageUrl );
		box.backgroundSize =
			width === 'auto' && height === 'auto'
				? 'cover'
				: `${ width } ${ height }`;
		box.backgroundPosition = 'center';
		box.backgroundRepeat = 'no-repeat';
	} else if ( type === 'none' ) {
		box.backgroundColor = 'transparent';
		box.backgroundImage = 'none';
	}

	const border = style.boxBorderStyle;
	if ( border === 'none' ) {
		box.borderStyle = 'none';
		box.outlineStyle = 'none';
	} else if (
		border === 'solid' ||
		border === 'dashed' ||
		border === 'dotted'
	) {
		box.borderStyle = border;
		box.borderWidth = px( style.boxBorderWidth ) || '1px';
		box.borderColor = style.boxBorderColor;
		box.outlineStyle = 'none';
	}

	const radius = px( style.boxRadius );
	if ( radius ) {
		box.borderRadius = radius;
	}
	if ( sized ) {
		const width = px( style.boxWidth );
		const height = px( style.boxHeight );
		if ( width ) {
			box.minWidth = width;
		}
		if ( height ) {
			box.minHeight = height;
		}
	}
	return box;
};

const copyToClipboard = async ( text: string ): Promise< void > => {
	if ( navigator.clipboard && window.isSecureContext ) {
		await navigator.clipboard.writeText( text );
		return;
	}
	// Fallback for non-HTTPS sites.
	const textarea = document.createElement( 'textarea' );
	textarea.value = text;
	textarea.setAttribute( 'readonly', '' );
	textarea.style.position = 'fixed';
	textarea.style.opacity = '0';
	document.body.appendChild( textarea );
	textarea.select();
	document.execCommand( 'copy' );
	document.body.removeChild( textarea );
};

/**
 * Click-to-copy coupon. Variants (Figma coupon v1–v6):
 * - ticket: code on the ticket image, label above (v1)
 * - card: label + dashed code box on a card with an accent edge (v2)
 * - pillcopy: orange pill, code + "COPY" button inside (v3)
 * - glossy: glossy orange button with copy icon, label above (v4)
 * - inline: "Label: CODE" as plain text (v6)
 * - dashed, pill: simple colored boxes
 */
const Coupon: React.FC< CouponProps > = ( {
	settings,
	label,
	code,
	copiedText,
	copyButton,
	ticket,
	ticketUrl,
	boxImage,
	boxImageUrl = '',
} ) => {
	const [ isCopied, setIsCopied ] = useState( false );

	useEffect( () => {
		if ( ! isCopied ) {
			return;
		}
		const timeout = window.setTimeout( () => setIsCopied( false ), 2000 );
		return () => window.clearTimeout( timeout );
	}, [ isCopied ] );

	if ( ! code.text ) {
		return null;
	}

	const { variant, style } = settings;
	const showCopied = isCopied && copiedText.isVisible && copiedText.text;

	// Layouts: the label sits above (default), beside the code (card,
	// inline) or inside the button (pill with copy button).
	const isCard = variant === 'card';
	const isInline = variant === 'inline';
	const labelInside = variant === 'pillcopy';
	const showLabel = label.isVisible && !! label.text;

	let wrapperStyle: CSSProperties | undefined;
	let buttonStyle: CSSProperties = {};
	if ( isCard ) {
		wrapperStyle = {
			backgroundColor: style.cardColor,
			borderLeftColor: style.accentColor,
		};
		buttonStyle = {
			backgroundColor: style.codeBgColor,
			borderColor: style.accentColor,
		};
	} else if ( variant === 'ticket' ) {
		buttonStyle = {
			backgroundImage: ticketUrl ? cssUrl( ticketUrl ) : undefined,
			...getImageStyle( ticket ),
		};
	} else if ( variant === 'glossy' ) {
		buttonStyle = { backgroundImage: cssUrl( glossyButton ) };
	} else if ( variant === 'dashed' ) {
		buttonStyle = {
			backgroundColor: style.bgColor,
			'--sb-coupon-line': style.borderColor,
		} as CSSProperties;
	} else if ( variant === 'pill' ) {
		buttonStyle = { backgroundColor: style.bgColor };
	} else if ( variant === 'dashcopy' ) {
		// Figma dark dashed box with copy button (node 10343:1113).
		buttonStyle = {
			backgroundColor: style.bgColor,
			'--sb-coupon-line': style.borderColor,
		} as CSSProperties;
	} else if ( variant === 'gradpill' ) {
		// Figma "Use Code" pill (node 10343:1096).
		buttonStyle = {
			backgroundImage: `linear-gradient(180deg, ${ style.pillFrom } 0%, ${ style.pillTo } 100%)`,
		};
	}

	// Code box variants: padding field and the code's alignment.
	const codeText = showCopied ? copiedText : code;
	if ( variant === 'gradpill' ) {
		// Code on the left, copy button on the right.
		buttonStyle.justifyContent = 'space-between';
	} else if ( variant !== 'inline' ) {
		buttonStyle.justifyContent = alignToFlex( codeText.align );
	}
	if ( variant !== 'ticket' && variant !== 'inline' ) {
		Object.assign( buttonStyle, getPaddingStyle( style ) );
	}
	if ( ! isInline ) {
		Object.assign(
			buttonStyle,
			getBoxStyle( style, boxImage, boxImageUrl, variant !== 'ticket' )
		);
	}

	// Coupon area (label + code box).
	const areaStyle: CSSProperties = {
		...getPaddingStyle( style, 'areaPadding' ),
	};
	if ( ! isCard && isHex( style.areaBg ) ) {
		areaStyle.backgroundColor = style.areaBg;
	}
	if ( px( style.areaRadius ) ) {
		areaStyle.borderRadius = px( style.areaRadius );
	}

	// The label above the code (column layout) can be aligned.
	const labelAbove = ! isCard && ! isInline && ! labelInside;

	// Hover (anywhere on the coupon): only what this style can show.
	const hoverKeys: HoverKey[] = [ 'hoverText', 'hoverLabel', 'hoverArea' ];
	if ( ! isInline ) {
		hoverKeys.push( 'hoverBg', 'hoverBorder' );
	}
	const hover = getHoverProps( style, hoverKeys );
	const effect = [ 'lift', 'grow', 'none' ].includes( style.hoverEffect )
		? style.hoverEffect
		: 'dim';

	const wrapperClass = [
		'sb-coupon',
		`is-${ variant }`,
		isCard && 'is-card',
		isInline && 'is-inline',
	]
		.filter( Boolean )
		.join( ' ' );

	return (
		<div
			className={ `${ wrapperClass }${ hover.className } is-hover-${ effect }` }
			style={ {
				...wrapperStyle,
				...areaStyle,
				...getMarginStyle( style ),
				...hover.style,
			} }
		>
			{ showLabel && ! labelInside && (
				<p
					className="sb-coupon-label"
					style={ {
						...getTextStyle( label ),
						alignSelf: labelAbove
							? alignToFlex( label.align )
							: undefined,
					} }
				>
					<TextFill text={ label }>{ label.text }</TextFill>
				</p>
			) }
			<button
				type="button"
				className={ `sb-coupon-code is-${ variant }` }
				style={ buttonStyle }
				onClick={ () =>
					copyToClipboard( code.text )
						.then( () => setIsCopied( true ) )
						.catch( () => setIsCopied( false ) )
				}
				title="Click to copy coupon code"
				aria-label={ `Copy coupon code ${ code.text }` }
			>
				{ variant === 'pillcopy' && (
					<img
						className="sb-coupon-sparkle"
						src={ sparkleIcon }
						alt=""
					/>
				) }
				{ variant === 'glossy' && (
					<img className="sb-coupon-icon" src={ copyIcon } alt="" />
				) }
				<span
					className="sb-coupon-text"
					style={ {
						...getTextStyle( codeText ),
						// Ticket: the code's own box inside the image.
						justifyContent:
							variant === 'ticket'
								? alignToFlex( codeText.align )
								: undefined,
					} }
					aria-live="polite"
				>
					<TextFill text={ codeText }>{ codeText.text }</TextFill>
				</span>
				{ ( variant === 'gradpill' || variant === 'dashcopy' ) &&
					copyButton?.isVisible &&
					copyButton.text && (
						<span
							className="sb-coupon-pillbtn"
							style={ {
								...getTextStyle( copyButton ),
								backgroundColor: style.copyBgColor,
							} }
						>
							<TextFill text={ copyButton }>
								{ copyButton.text }
							</TextFill>
						</span>
					) }
				{ showLabel && labelInside && (
					<span
						className="sb-coupon-copy"
						style={ {
							...getTextStyle( label ),
							backgroundColor: style.copyBgColor,
						} }
					>
						<TextFill text={ label }>{ label.text }</TextFill>
					</span>
				) }
			</button>
		</div>
	);
};

export default Coupon;
